import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { LogOut, Plus, Edit3, Trash2, Users, BookOpen, TrendingUp, Package, Clock, Key, CheckSquare, FileText, Settings } from 'lucide-react';
import ChecklistManagement from '../components/ChecklistManagement';
import ContentManagement from '../components/ContentManagement';
import SettingsManagement from '../components/SettingsManagement';

function AdminDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('recipes');
  const [recipes, setRecipes] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Recipe dialog state
  const [showRecipeDialog, setShowRecipeDialog] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [recipeForm, setRecipeForm] = useState({
    name: '',
    category: '',
    recipe_type: 'batch_prep',
    limiting_ingredient_name: '',
    notes: '',
  });
  const [ingredients, setIngredients] = useState([]);
  const [sopSteps, setSopSteps] = useState(['']);
  const [submitting, setSubmitting] = useState(false);

  // User creation dialog state
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [userForm, setUserForm] = useState({ username: '', password: '', role: 'staff' });
  const [creatingUser, setCreatingUser] = useState(false);

  // Password change dialog state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Batch edit dialog state
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [batchForm, setBatchForm] = useState({ actual_yield: '', notes: '' });
  const [updatingBatch, setUpdatingBatch] = useState(false);
  const [allBatches, setAllBatches] = useState([]);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'recipes') {
        const response = await axios.get(`${API}/recipes`);
        setRecipes(response.data);
      } else if (activeTab === 'users') {
        const response = await axios.get(`${API}/users`);
        setUsers(response.data);
      } else if (activeTab === 'stats') {
        const [statsRes, batchesRes, recipesRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/batches`),
          axios.get(`${API}/recipes`)
        ]);
        setStats(statsRes.data);
        setAllBatches(batchesRes.data);
        setRecipes(recipesRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Recipe CRUD
  const openRecipeDialog = (recipe = null) => {
    if (recipe) {
      setEditingRecipe(recipe);
      setRecipeForm({
        name: recipe.name,
        category: recipe.category,
        recipe_type: recipe.recipe_type || 'batch_prep',
        limiting_ingredient_name: recipe.limiting_ingredient_name || '',
        notes: recipe.notes || '',
      });
      setIngredients(recipe.ingredients);
      setSopSteps(recipe.sop_steps);
    } else {
      setEditingRecipe(null);
      setRecipeForm({ name: '', category: '', recipe_type: 'batch_prep', limiting_ingredient_name: '', notes: '' });
      setIngredients([]);
      setSopSteps(['']);
    }
    setShowRecipeDialog(true);
  };

  const addIngredient = () => {
    setIngredients([...ingredients, { name: '', quantity: '', unit: '', percentage: '', is_limiting: false }]);
  };

  const updateIngredient = (index, field, value) => {
    const updated = [...ingredients];
    if (field === 'quantity' || field === 'percentage') {
      updated[index][field] = value === '' ? '' : parseFloat(value) || 0;
    } else {
      updated[index][field] = value;
    }
    setIngredients(updated);
  };

  const removeIngredient = (index) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const addSopStep = () => {
    setSopSteps([...sopSteps, '']);
  };

  const updateSopStep = (index, value) => {
    const updated = [...sopSteps];
    updated[index] = value;
    setSopSteps(updated);
  };

  const removeSopStep = (index) => {
    setSopSteps(sopSteps.filter((_, i) => i !== index));
  };

  const submitRecipe = async () => {
    if (!recipeForm.name || !recipeForm.category || ingredients.length === 0 || sopSteps.filter(s => s).length === 0) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...recipeForm,
        ingredients: ingredients.map(ing => ({
          name: ing.name,
          quantity: parseFloat(ing.quantity) || 0,
          unit: ing.unit,
          percentage: ing.percentage ? parseFloat(ing.percentage) : null,
          is_limiting: ing.is_limiting || false,
        })),
        sop_steps: sopSteps.filter(step => step.trim() !== ''),
      };

      if (editingRecipe) {
        await axios.put(`${API}/recipes/${editingRecipe.id}`, payload);
        toast.success('Recipe updated!');
      } else {
        await axios.post(`${API}/recipes`, payload);
        toast.success('Recipe created!');
      }

      setShowRecipeDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save recipe');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteRecipe = async (recipeId) => {
    if (!window.confirm('Are you sure you want to delete this recipe?')) return;

    try {
      await axios.delete(`${API}/recipes/${recipeId}`);
      toast.success('Recipe deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete recipe');
    }
  };

  // User management
  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User deleted!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const toggleUserRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'staff' : 'admin';
    
    try {
      await axios.patch(`${API}/users/${userId}/role?role=${newRole}`);
      toast.success(`User role updated to ${newRole}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const createUser = async () => {
    if (!userForm.username || !userForm.password) {
      toast.error('Please fill all fields');
      return;
    }

    setCreatingUser(true);
    try {
      await axios.post(`${API}/auth/register`, userForm);
      toast.success('User created successfully!');
      setShowUserDialog(false);
      setUserForm({ username: '', password: '', role: 'staff' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  const openPasswordDialog = (userObj) => {
    setSelectedUser(userObj);
    setNewPassword('');
    setShowPasswordDialog(true);
  };

  const changePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.patch(`${API}/users/${selectedUser.id}/password`, {
        new_password: newPassword
      });
      toast.success('Password changed successfully!');
      setShowPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const openBatchDialog = (batch) => {
    setEditingBatch(batch);
    setBatchForm({
      actual_yield: batch.actual_yield || '',
      notes: batch.notes || ''
    });
    setShowBatchDialog(true);
  };

  const updateBatch = async () => {
    if (!batchForm.actual_yield) {
      toast.error('Please enter actual yield');
      return;
    }

    setUpdatingBatch(true);
    try {
      await axios.patch(`${API}/batches/${editingBatch.id}`, {
        actual_yield: parseFloat(batchForm.actual_yield),
        notes: batchForm.notes
      });
      toast.success('Batch updated successfully!');
      setShowBatchDialog(false);
      setEditingBatch(null);
      setBatchForm({ actual_yield: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to update batch');
    } finally {
      setUpdatingBatch(false);
    }
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this batch record?')) return;

    try {
      await axios.delete(`${API}/batches/${batchId}`);
      toast.success('Batch deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete batch');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      {/* Header */}
      <header className="glass border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/acai-theory-logo.png" 
              alt="Acai Theory" 
              className="h-10 w-auto"
            />
            <div>
              <h1 className="text-sm font-semibold text-gray-900">Admin</h1>
              <p className="text-xs text-gray-500">@{user.username}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/batches')}
              data-testid="admin-view-batches-button"
              className="rounded-xl"
            >
              <Clock className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="rounded-xl"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6 pb-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-7 mb-6">
            <TabsTrigger value="stats" data-testid="stats-tab">
              <TrendingUp className="h-4 w-4 mr-2" />
              Stats
            </TabsTrigger>
            <TabsTrigger value="recipes" data-testid="recipes-tab">
              <BookOpen className="h-4 w-4 mr-2" />
              Recipes
            </TabsTrigger>
            <TabsTrigger value="batches" data-testid="batches-tab">
              <Package className="h-4 w-4 mr-2" />
              Batches
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="checklists" data-testid="checklists-tab">
              <CheckSquare className="h-4 w-4 mr-2" />
              Checklists
            </TabsTrigger>
            <TabsTrigger value="content" data-testid="content-tab">
              <FileText className="h-4 w-4 mr-2" />
              Content
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Stats Tab */}
          <TabsContent value="stats">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : stats ? (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white rounded-2xl p-6 shadow-md border border-purple-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                        <BookOpen className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Recipes</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.total_recipes}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md border border-green-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                        <Package className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Batches</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.total_batches}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md border border-blue-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                        <Users className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Users</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.total_users}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-bold text-lg text-gray-900">Recent Batches</h2>
                    <Button
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/batches/export/excel`, {
                            responseType: 'blob'
                          });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `batch_records_${new Date().toISOString().split('T')[0]}.xlsx`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          toast.success('Excel file downloaded!');
                        } catch (error) {
                          toast.error('Failed to download Excel');
                        }
                      }}
                      data-testid="download-excel-button"
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                    >
                      📥 Download Excel
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {stats.recent_batches.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No batches yet</p>
                    ) : (
                      stats.recent_batches.map((batch) => (
                        <div key={batch.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
                          <div>
                            <p className="font-medium text-gray-900">@{batch.staff_username || 'Unknown'}</p>
                            <p className="text-xs text-gray-500">{new Date(batch.prepared_at).toLocaleString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">
                              {batch.actual_yield || batch.expected_yield}g
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </TabsContent>

          {/* Recipes Tab */}
          <TabsContent value="recipes">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Manage Recipes</h2>
              <Button
                onClick={() => openRecipeDialog()}
                data-testid="create-recipe-button"
                className="bg-primary hover:bg-primary-light text-white font-semibold rounded-xl transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Recipe
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : recipes.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-500 text-lg">No recipes yet</p>
              </div>
            ) : (
              <div className="recipe-grid">
                {recipes.map((recipe) => (
                  <div
                    key={recipe.id}
                    data-testid={`admin-recipe-card-${recipe.id}`}
                    className="bg-white rounded-2xl p-5 shadow-md border border-gray-100"
                  >
                    <div 
                      onClick={() => navigate(`/recipe/${recipe.id}`)}
                      className="cursor-pointer hover:bg-gray-50 -m-5 p-5 rounded-2xl transition-colors mb-2"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-bold text-lg text-gray-900 mb-1">{recipe.name}</h3>
                          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                            {recipe.category}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>{recipe.ingredients.length} ingredients</p>
                        <p>{recipe.sop_steps.length} steps</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openRecipeDialog(recipe)}
                        data-testid={`edit-recipe-${recipe.id}-button`}
                        className="flex-1 rounded-xl"
                      >
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteRecipe(recipe.id)}
                        data-testid={`delete-recipe-${recipe.id}-button`}
                        className="flex-1 rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Batches Tab */}
          <TabsContent value="batches">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900">All Batch Records</h2>
              <p className="text-sm text-gray-500 mt-1">View and edit all batch preparation records</p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : allBatches.length === 0 ? (
              <div className="text-center py-20">
                <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No batches recorded yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {allBatches.map((batch) => {
                  const recipe = recipes.find(r => r.id === batch.recipe_id);
                  const variance = batch.actual_yield && batch.expected_yield
                    ? ((batch.actual_yield - batch.expected_yield) / batch.expected_yield) * 100
                    : null;

                  return (
                    <div
                      key={batch.id}
                      data-testid={`admin-batch-${batch.id}`}
                      className="bg-white rounded-2xl p-5 shadow-md border border-gray-100"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-bold text-lg text-gray-900 mb-1">
                            {recipe?.name || 'Unknown Recipe'}
                          </h3>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span>@{batch.staff_username || batch.staff_email || 'Unknown'}</span>
                            <span>•</span>
                            <span>{new Date(batch.prepared_at).toLocaleString()}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openBatchDialog(batch)}
                            data-testid={`edit-batch-${batch.id}-button`}
                            className="rounded-xl"
                          >
                            <Edit3 className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteBatch(batch.id)}
                            data-testid={`delete-batch-${batch.id}-button`}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="bg-primary/10 rounded-xl p-3">
                          <p className="text-xs text-primary font-medium mb-1">Expected Yield</p>
                          <p className="text-xl font-bold text-gray-900">{batch.expected_yield}g</p>
                        </div>
                        <div className={`rounded-xl p-3 ${
                          batch.actual_yield ? 'bg-green-50' : 'bg-gray-50'
                        }`}>
                          <p className={`text-xs font-medium mb-1 ${
                            batch.actual_yield ? 'text-green-600' : 'text-gray-500'
                          }`}>
                            Actual Yield
                          </p>
                          <p className={`text-xl font-bold ${
                            batch.actual_yield ? 'text-green-900' : 'text-gray-400'
                          }`}>
                            {batch.actual_yield ? `${batch.actual_yield}g` : 'Pending'}
                          </p>
                        </div>
                      </div>

                      {variance !== null && (
                        <div className="mt-3 flex items-center justify-between p-3 rounded-xl bg-blue-50">
                          <span className="text-sm text-blue-700 font-medium">Variance</span>
                          <span className={`text-sm font-bold ${
                            variance > 0 ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {variance > 0 ? '+' : ''}{variance.toFixed(2)}%
                          </span>
                        </div>
                      )}

                      {batch.notes && (
                        <div className="mt-3 p-3 rounded-xl bg-gray-50">
                          <p className="text-xs text-gray-500 mb-1">Notes</p>
                          <p className="text-sm text-gray-700">{batch.notes}</p>
                        </div>
                      )}

                      {batch.recorded_at && (
                        <div className="mt-3 text-xs text-gray-500">
                          Recorded: {new Date(batch.recorded_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Manage Users</h2>
              <Button
                onClick={() => setShowUserDialog(true)}
                data-testid="create-user-button"
                className="bg-primary hover:bg-primary-light text-white font-semibold rounded-xl transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((u) => (
                  <div
                    key={u.id}
                    data-testid={`user-card-${u.id}`}
                    className="bg-white rounded-2xl p-5 shadow-md border border-gray-100 flex items-center justify-between"
                  >
                    <div>
                      <p className="font-semibold text-gray-900">@{u.username}</p>
                      <p className="text-sm text-gray-500">
                        Joined {new Date(u.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleUserRole(u.id, u.role)}
                        data-testid={`toggle-role-${u.id}-button`}
                        className={`px-4 py-2 rounded-xl font-semibold text-sm ${
                          u.role === 'admin'
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {u.role}
                      </button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPasswordDialog(u)}
                        data-testid={`change-password-${u.id}-button`}
                        className="rounded-xl"
                      >
                        <Key className="h-4 w-4 mr-1" />
                        Password
                      </Button>
                      {u.id !== user.id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteUser(u.id);
                          }}
                          data-testid={`delete-user-${u.id}-button`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Checklists Tab */}
          <TabsContent value="checklists">
            <ChecklistManagement />
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content">
            <ContentManagement />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <SettingsManagement />
          </TabsContent>
        </Tabs>
      </main>

      {/* Batch Edit Dialog */}
      <Dialog open={showBatchDialog} onOpenChange={setShowBatchDialog}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>Edit Batch Record</DialogTitle>
            <DialogDescription>
              Update batch details and actual yield
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="batch-actual-yield">Actual Yield (grams) *</Label>
              <Input
                id="batch-actual-yield"
                data-testid="batch-actual-yield-input"
                type="number"
                step="0.01"
                placeholder="Enter actual yield"
                value={batchForm.actual_yield}
                onChange={(e) => setBatchForm({ ...batchForm, actual_yield: e.target.value })}
                className="h-12 mt-2 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="batch-notes">Notes (Optional)</Label>
              <textarea
                id="batch-notes"
                data-testid="batch-notes-input"
                placeholder="Any observations or notes..."
                value={batchForm.notes}
                onChange={(e) => setBatchForm({ ...batchForm, notes: e.target.value })}
                className="w-full h-24 mt-2 px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <Button
              onClick={updateBatch}
              data-testid="submit-batch-update-button"
              className="w-full h-12 bg-primary hover:bg-primary-light text-white font-semibold rounded-xl transition-colors"
              disabled={updatingBatch}
            >
              {updatingBatch ? 'Updating...' : 'Update Batch'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Change password for @{selectedUser?.username}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="new-password">New Password *</Label>
              <Input
                id="new-password"
                data-testid="new-password-input"
                type="text"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="h-12 mt-2 rounded-xl"
              />
              <p className="text-xs text-gray-500 mt-1">User will need to login with this new password</p>
            </div>
            <Button
              onClick={changePassword}
              data-testid="submit-password-change-button"
              className="w-full h-12 bg-primary hover:bg-primary-light text-white font-semibold rounded-xl transition-colors"
              disabled={changingPassword}
            >
              {changingPassword ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Creation Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>Add a new staff member or admin to the system</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="user-username">Username *</Label>
              <Input
                id="user-username"
                data-testid="user-username-input"
                type="text"
                placeholder="staff_name"
                value={userForm.username}
                onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                className="h-12 mt-2 rounded-xl"
              />
              <p className="text-xs text-gray-500 mt-1">Username should be simple (e.g., john, staff1)</p>
            </div>
            <div>
              <Label htmlFor="user-password">Password *</Label>
              <Input
                id="user-password"
                data-testid="user-password-input"
                type="text"
                placeholder="Create password for user"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                className="h-12 mt-2 rounded-xl"
              />
              <p className="text-xs text-gray-500 mt-1">Share this password with the user securely</p>
            </div>
            <div>
              <Label htmlFor="user-role">Role *</Label>
              <select
                id="user-role"
                data-testid="user-role-select"
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                className="w-full h-12 px-3 mt-2 rounded-xl border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <Button
              onClick={createUser}
              data-testid="submit-user-button"
              className="w-full h-12 bg-primary hover:bg-primary-light text-white font-semibold rounded-xl transition-colors"
              disabled={creatingUser}
            >
              {creatingUser ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recipe Dialog */}
      <Dialog open={showRecipeDialog} onOpenChange={setShowRecipeDialog}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRecipe ? 'Edit Recipe' : 'Create Recipe'}</DialogTitle>
            <DialogDescription>Fill in the recipe details</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="recipe-name">Recipe Name *</Label>
                <Input
                  id="recipe-name"
                  data-testid="recipe-name-input"
                  value={recipeForm.name}
                  onChange={(e) => setRecipeForm({ ...recipeForm, name: e.target.value })}
                  placeholder="e.g., Acai Sorbet"
                  className="h-12 mt-2 rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="recipe-category">Category *</Label>
                <Input
                  id="recipe-category"
                  data-testid="recipe-category-input"
                  value={recipeForm.category}
                  onChange={(e) => setRecipeForm({ ...recipeForm, category: e.target.value })}
                  placeholder="e.g., Sorbet, Bowl, Smoothie"
                  className="h-12 mt-2 rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="recipe-type">Recipe Type *</Label>
                <select
                  id="recipe-type"
                  data-testid="recipe-type-select"
                  value={recipeForm.recipe_type}
                  onChange={(e) => setRecipeForm({ ...recipeForm, recipe_type: e.target.value })}
                  className="w-full h-12 px-3 mt-2 rounded-xl border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="batch_prep">⚗️ Batch Prep (shows ingredients, scaling, batch logging)</option>
                  <option value="assembly">🍽️ Assembly (shows only steps, no scaling)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Batch Prep: For recipes that need ingredient scaling and batch tracking. 
                  Assembly: For serving/plating instructions only.
                </p>
              </div>
              <div>
                <Label htmlFor="limiting-ingredient">Limiting Ingredient {recipeForm.recipe_type === 'batch_prep' ? '(Optional)' : '(Not applicable for assembly)'}</Label>
                <Input
                  id="limiting-ingredient"
                  data-testid="limiting-ingredient-input"
                  value={recipeForm.limiting_ingredient_name}
                  onChange={(e) => setRecipeForm({ ...recipeForm, limiting_ingredient_name: e.target.value })}
                  placeholder="e.g., Frozen Strawberry"
                  className="h-12 mt-2 rounded-xl"
                  disabled={recipeForm.recipe_type === 'assembly'}
                />
              </div>
              <div>
                <Label htmlFor="recipe-notes">Notes (Optional)</Label>
                <textarea
                  id="recipe-notes"
                  data-testid="recipe-notes-input"
                  value={recipeForm.notes}
                  onChange={(e) => setRecipeForm({ ...recipeForm, notes: e.target.value })}
                  placeholder="Additional notes..."
                  className="w-full h-20 mt-2 px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            {/* Ingredients */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>Ingredients *</Label>
                <Button
                  type="button"
                  size="sm"
                  onClick={addIngredient}
                  data-testid="add-ingredient-button"
                  className="rounded-xl"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-3">
                {ingredients.map((ing, idx) => (
                  <div key={idx} className="flex gap-2 items-start p-3 bg-gray-50 rounded-xl">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Name"
                        data-testid={`ingredient-${idx}-name`}
                        value={ing.name}
                        onChange={(e) => updateIngredient(idx, 'name', e.target.value)}
                        className="h-10"
                      />
                      <Input
                        placeholder="Unit"
                        data-testid={`ingredient-${idx}-unit`}
                        value={ing.unit}
                        onChange={(e) => updateIngredient(idx, 'unit', e.target.value)}
                        className="h-10"
                      />
                      <Input
                        type="number"
                        placeholder="Quantity"
                        data-testid={`ingredient-${idx}-quantity`}
                        value={ing.quantity}
                        onChange={(e) => updateIngredient(idx, 'quantity', e.target.value)}
                        className="h-10"
                      />
                      <Input
                        type="number"
                        placeholder="% (optional)"
                        data-testid={`ingredient-${idx}-percentage`}
                        value={ing.percentage}
                        onChange={(e) => updateIngredient(idx, 'percentage', e.target.value)}
                        className="h-10"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeIngredient(idx)}
                      data-testid={`remove-ingredient-${idx}`}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* SOP Steps */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>Preparation Steps *</Label>
                <Button
                  type="button"
                  size="sm"
                  onClick={addSopStep}
                  data-testid="add-sop-step-button"
                  className="rounded-xl"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-3">
                {sopSteps.map((step, idx) => (
                  <div key={idx} className="flex gap-2 items-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-sm mt-2">
                      {idx + 1}
                    </div>
                    <textarea
                      placeholder="Describe this step..."
                      data-testid={`sop-step-${idx}`}
                      value={step}
                      onChange={(e) => updateSopStep(idx, e.target.value)}
                      className="flex-1 h-20 px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeSopStep(idx)}
                      data-testid={`remove-sop-${idx}`}
                      className="text-red-600 hover:text-red-700 mt-2"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Submit */}
            <Button
              onClick={submitRecipe}
              data-testid="submit-recipe-button"
              className="w-full h-12 bg-primary hover:bg-primary-light text-white font-semibold rounded-xl transition-colors"
              disabled={submitting}
            >
              {submitting ? 'Saving...' : editingRecipe ? 'Update Recipe' : 'Create Recipe'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminDashboard;
